import { useState, useEffect, useCallback } from "react";
import { cn } from "~/lib/utils";
import closeIcon from "~/assets/search/x-circle.svg";
import searchIcon from "~/assets/search/search.svg";
import { useMySearchStore } from "~/lib/stores/my-search";
import hotIcon from "~/assets/search/fire.svg";
import { dealApi } from "~/api/deal";
import searchGreyIcon from "~/assets/search/search-grey.svg";
import {
  SearchSuggestionsSkeleton,
  SearchTrendingKeywordsSkeleton,
} from "~/components/skeleton/SearchSuggestionsSkeleton";

export interface SearchInputProps {
  onSearch: (keyword: string) => void;
  onClose: () => void;
}
export default function SearchInput({ onSearch, onClose }: SearchInputProps) {
  const [keywordInput, setKeywordInput] = useState("");

  const keyword = keywordInput.trim();

  useDisableScroll();

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  return (
    <div
      id="search-input"
      className={cn(
        "absolute top-0 left-0 z-52",
        "w-full max-w-[516px] bg-white",
        "rounded-[24px] border border-[#D4D4D4]",
        "flex flex-col gap-[10px]",
        "px-[12px] pt-[8px] pb-[16px]"
      )}
    >
      <SearchInputForm
        keywordInput={keywordInput}
        setKeywordInput={setKeywordInput}
        onSearch={keyword => {
          setKeywordInput(keyword);
          onSearch(keyword);
        }}
      />
      <div className="w-full flex-1 border-t-1 border-[#0000001A]" />
      {keyword.length > 0 ? (
        <SearchSuggestions
          keyword={keyword}
          onSearch={suggestion => {
            setKeywordInput(suggestion);
            onSearch(suggestion);
          }}
        />
      ) : (
        <SearchTrendingKeywords
          onSearch={trendingKeyword => {
            setKeywordInput(trendingKeyword);
            onSearch(trendingKeyword);
          }}
        />
      )}
    </div>
  );
}

function useDisableScroll() {
  useEffect(() => {
    // Add a class that a global CSS rule can target
    document.body.classList.add("overflow-hidden");

    // Remove the class on cleanup
    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, []);
}

function SearchInputForm({
  keywordInput,
  setKeywordInput,
  onSearch,
}: {
  keywordInput: string;
  setKeywordInput: (keyword: string) => void;
  onSearch: (keyword: string) => void;
}) {
  return (
    <form
      className="relative h-[36px] w-full"
      onSubmit={e => {
        e.preventDefault();
        onSearch(keywordInput);
      }}
    >
      <input
        className="h-[36px] w-full border-none shadow-none outline-none"
        autoFocus
        type="text"
        value={keywordInput}
        onChange={e => setKeywordInput(e.target.value)}
      />
      <div className="absolute top-0 right-0 flex h-[36px] w-[62px] items-center justify-center gap-[10px]">
        <button
          id="deal-search-clear-button"
          type="button"
          onClick={() => setKeywordInput("")}
          className="cursor-pointer"
        >
          <img src={closeIcon} alt="close" className="h-[16px] w-[16px]" />
        </button>

        <button
          id="deal-search-submit-button"
          type="submit"
          className="h-[36px] w-[36px] cursor-pointer rounded-[12px] bg-gradient-to-r from-[#FFF6B5] to-[#FDF853] p-[8px]"
        >
          <img src={searchIcon} alt="search" className="m-auto h-[18px] w-[18px]" />
        </button>
      </div>
    </form>
  );
}

function SearchTrendingKeywords({ onSearch }: { onSearch: (keyword: string) => void }) {
  const { hotKeywords, fetchMySearchKeywords, isLoading } = useMySearchStore();

  useEffect(() => {
    fetchMySearchKeywords();
  }, [fetchMySearchKeywords]);

  if (isLoading) {
    return <SearchTrendingKeywordsSkeleton />;
  }

  return (
    <div className="flex flex-row flex-wrap gap-[10px]">
      {hotKeywords?.keywords.map((keyword, index) => (
        <button
          key={`${keyword}-${index}`}
          id={`deal-search-keyword-${index}`}
          className="inline-flex h-[28px] cursor-pointer items-center justify-start gap-1 rounded-[12px] bg-[#F2F2F2] px-[8px] py-[4px]"
          onClick={() => onSearch(keyword)}
        >
          {index === 0 && <img src={hotIcon} alt="hot" className="h-[16px] w-[16px]" />}
          <span className="justify-start text-sm leading-tight font-medium text-neutral-700">{keyword}</span>
        </button>
      ))}
    </div>
  );
}

function SearchSuggestions({ keyword, onSearch }: { keyword: string; onSearch: (keyword: string) => void }) {
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const debouncedKeyword = useDebounce(keyword, 500);

  const fetchSuggestions = useCallback(async () => {
    if (debouncedKeyword.length > 0) {
      setLoading(true);
      const response = await dealApi.getSearchSuggestion({ q: debouncedKeyword });
      setSuggestions(response.suggestions || []);
      setLoading(false);
    }
  }, [debouncedKeyword]);

  useEffect(() => {
    fetchSuggestions();
  }, [fetchSuggestions]);

  if (loading) {
    return <SearchSuggestionsSkeleton />;
  }

  return (
    <div className="flex flex-col items-start justify-start gap-[10px]">
      {suggestions.map((suggestion, index) => (
        <button
          key={`${suggestion}-${index}`}
          id={`deal-search-suggestion-${index}`}
          onClick={() => onSearch(suggestion)}
          className="inline-flex w-full cursor-pointer items-center justify-start gap-1 hover:bg-neutral-100"
        >
          <img src={searchGreyIcon} alt="search" className="h-[16px] w-[16px]" />
          <span className="justify-start text-sm leading-tight font-medium text-neutral-700">{suggestion}</span>
        </button>
      ))}
    </div>
  );
}

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  return debouncedValue;
}
